import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { X, AlertTriangle, CheckCircle2, UserPlus } from 'lucide-react';
import { Context } from 'Context/Context';
import { outreachEmailCheckApi } from 'Constants';
import QcrLoader from 'Components/Icons/QcrLoader';
import { TEMPLATES, buildEmail, caseBlurb, isEmail } from './outreachTemplates';

const apiMessage = (err) => err?.response?.data?.message || 'Something went wrong. Try again.';

// Compose and send the outreach email to one firm.
// onSend({ email, template, subject, body, caseIds, mode }) must return a Promise.
// The address is checked with the server as it's typed:
//   claim   - no account: "Claim your firm →" email with an invite link
//   login   - one of this firm's own accounts: same email with "Log in →"
//   blocked - someone else's account, or the firm already has an account
//             (shows the firm's account email to use instead)
function OutreachEmailModal({ open, onClose, firm, cases, email, onEmailChange, canUseSaved, sendingReady, onSend }) {
  const { makeApiCallFunc } = useContext(Context);
  const [check, setCheck] = useState(null);       // server answer for the current address
  const [checking, setChecking] = useState(false);
  const checkSeq = useRef(0);
  const builtFor = useRef('');                    // which kind of email the text was last built for
  const [template, setTemplate] = useState('recommended');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [touchedEmail, setTouchedEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  // website clicks from clients' firm matches (By firm: a count; By case: clicked or not)
  const clicks = firm?.clickedCount || (firm?.clickedByClient ? 1 : 0);

  const account = check?.mode === 'login' ? check.account : null;
  const kindKey = check && check.mode !== 'blocked' ? `${check.mode}:${account?.state || ''}:${account?.premium ? 1 : 0}` : '';

  useEffect(() => {
    if (!open) return;
    const t = canUseSaved ? 'saved' : 'recommended';
    const e = buildEmail(t, firm?.name, cases, { clicks, account });
    builtFor.current = kindKey;
    setTemplate(t); setSubject(e.subject); setBody(e.body);
    setTouchedEmail(false); setSending(false); setError('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, firm?.key]);

  const pick = (t) => {
    const e = buildEmail(t, firm?.name, cases, { clicks, account });
    builtFor.current = kindKey;
    setTemplate(t); setSubject(e.subject); setBody(e.body);
  };

  // ask the server which email this address gets (debounced while typing)
  const runCheck = (addr) => {
    const seq = ++checkSeq.current;
    if (!firm?.firmId || !isEmail(addr)) { setCheck(null); setChecking(false); return; }
    setChecking(true);
    makeApiCallFunc(outreachEmailCheckApi, { firmId: firm.firmId, email: String(addr).trim() },
      ({ data }) => { if (seq === checkSeq.current) { setCheck(data || null); setChecking(false); } },
      () => { if (seq === checkSeq.current) { setCheck(null); setChecking(false); } }, null, true);
  };
  useEffect(() => {
    if (!open) { checkSeq.current++; setCheck(null); setChecking(false); return undefined; }
    const t = setTimeout(() => runCheck(email), 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, email, firm?.firmId]);

  // claim <-> log in (or the account's state) changed: rebuild the text for it
  useEffect(() => {
    if (!open || !kindKey || kindKey === builtFor.current) return;
    const e = buildEmail(template, firm?.name, cases, { clicks, account });
    builtFor.current = kindKey;
    setSubject(e.subject); setBody(e.body);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindKey]);

  const emailOk = isEmail(email);
  const blocked = check?.mode === 'blocked';
  const canSend = sendingReady && emailOk && !!check && !blocked && !checking && kindKey === builtFor.current && subject.trim() && body.trim() && !sending;

  const send = () => {
    setTouchedEmail(true);
    if (!canSend) return;
    setSending(true); setError('');
    onSend({ email: email.trim(), template, subject: subject.trim(), body: body.trim(), caseIds: (cases || []).map((c) => c._id), mode: check.mode })
      .then(() => setSending(false))
      .catch((err) => {
        setSending(false); setError(apiMessage(err));
        // the address's account changed since we checked - check again (the text follows)
        if (['MODE_CHANGED', 'FIRM_HAS_ACCOUNT', 'OTHER_ACCOUNT'].includes(err?.response?.data?.code)) runCheck(email);
      });
  };

  const label = 'font-semibold text-[11px] leading-none tracking-[.08em] uppercase text-navy/[.55]';
  const field = 'w-full text-navy bg-white border rounded-md focus:outline-none focus:border-brass';

  return (
    <ReactModal
      isOpen={!!open}
      onRequestClose={sending ? undefined : onClose}
      style={{
        overlay: { zIndex: 50, backgroundColor: 'rgba(20,33,61,.45)' },
        content: {
          top: '50%', left: '50%', right: 'auto', bottom: 'auto', marginRight: '-50%', transform: 'translate(-50%, -50%)',
          width: 'calc(100vw - 32px)', maxWidth: '640px', maxHeight: 'calc(100vh - 48px)', padding: 0, border: 'none', borderRadius: '8px',
          background: '#f7f4ed', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px -20px rgba(20,33,61,.5)',
        },
      }}
    >
      <div className='font-plexsans flex-none flex items-start justify-between gap-4 py-[18px] px-[22px] bg-white border-b border-navy/[.1]'>
        <div className='flex flex-col gap-1.5 min-w-0'>
          <span className='font-plexmono font-medium text-[11px] leading-none tracking-[.08em] uppercase text-brass'>Firm outreach</span>
          <span className='font-spectral text-[19px] leading-[1.25] text-navy [overflow-wrap:anywhere]'>Email {firm?.name || 'firm'}</span>
        </div>
        <button type='button' onClick={onClose} disabled={sending} title='Close' className='flex bg-transparent border-0 cursor-pointer text-navy p-0.5 disabled:opacity-40'>
          <X className='w-[18px] h-[18px]' strokeWidth={2.2} />
        </button>
      </div>

      <div className='flex-1 min-h-0 overflow-y-auto py-5 px-[22px] font-plexsans flex flex-col gap-4'>
        {/* {!sendingReady &&
          <div className='flex items-start gap-2.5 p-3 rounded-md bg-brass/[.08] border border-brass/[.3] text-[12.5px] leading-[1.5] text-navy'>
            <AlertTriangle className='w-4 h-4 text-brass flex-none mt-px' />
            Sending is off until the SendGrid unsubscribe group is set up on the server (OUTREACH_UNSUBSCRIBE_GROUP_ID). You can still prepare and review the email.
          </div>} */}

        <label className='flex flex-col gap-1.5'>
          <span className={label}>To</span>
          <input type='email' value={email || ''} placeholder="Firm's email address"
            onChange={(e) => onEmailChange(e.target.value)} onBlur={() => setTouchedEmail(true)}
            className={`${field} h-11 px-3 text-[14px] ${touchedEmail && !emailOk ? 'border-danger' : 'border-navy/[.2]'}`} />
          {touchedEmail && !emailOk && <span className='text-[12px] text-danger'>Enter a valid email address.</span>}
          {emailOk && checking && <span className='text-[12px] text-navy/[.5]'>Checking this address…</span>}
          {emailOk && !checking && check?.mode === 'login' &&
            <span className='inline-flex items-center gap-1.5 text-[12px] font-medium text-success'>
              <CheckCircle2 className='w-3.5 h-3.5 flex-none' />
              Has an account{account?.state === 'review' ? ' (profile under review)' : ''} · gets the "Log in" email
            </span>}
          {emailOk && !checking && check?.mode === 'claim' &&
            <span className='inline-flex items-center gap-1.5 text-[12px] font-medium text-brass'>
              <UserPlus className='w-3.5 h-3.5 flex-none' />New to Quick Case Review · gets the "Claim your firm" email
            </span>}
          {emailOk && !checking && blocked &&
            <div className='flex flex-wrap items-center gap-x-3 gap-y-1.5 p-2.5 rounded-md bg-danger/[.06] border border-danger/[.25] text-[12.5px] leading-[1.5] text-danger'>
              <span className='inline-flex items-start gap-1.5'><AlertTriangle className='w-3.5 h-3.5 flex-none mt-[3px]' />{check.message}</span>
              {check.accountEmail && check.accountEmail !== String(email).trim().toLowerCase() &&
                <button type='button' onClick={() => onEmailChange(check.accountEmail)}
                  className='font-semibold text-[12.5px] text-brass underline bg-transparent border-0 p-0 cursor-pointer hover:text-brassDark'>Use {check.accountEmail}</button>}
            </div>}
          <span className='text-[12px] text-navy/[.5]'>Saved on this firm for next time.</span>
        </label>

        <div className='flex flex-col gap-1.5'>
          <span className={label}>Template</span>
          <div className='flex flex-wrap gap-2'>
            {Object.values(TEMPLATES).map((t) => {
              const disabled = t.key === 'saved' && !canUseSaved;
              const sel = template === t.key;
              return (
                <button key={t.key} type='button' disabled={disabled} onClick={() => pick(t.key)}
                  title={disabled ? 'No client has saved this firm yet' : undefined}
                  className={`font-medium text-[13px] leading-none border rounded-[20px] py-2.5 px-3.5 ${disabled ? 'opacity-40 cursor-not-allowed bg-white border-navy/[.15] text-navy' : sel ? 'cursor-pointer bg-navy border-navy text-ivory' : 'cursor-pointer bg-white border-navy/[.2] text-navy'}`}>
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className='flex flex-col gap-1.5'>
          <span className={label}>{cases?.length > 1 ? `${cases.length} cases included` : 'Case included'}</span>
          <div className='bg-white border border-navy/[.12] rounded-md px-3.5 py-1'>
            {(cases || []).map((d) => (
              <div key={d?._id} className='py-2.5 border-b border-navy/[.08] last:border-b-0 text-[13px] leading-[1.45] text-navy'>
                <span className='font-plexmono text-[11.5px] text-brass mr-2'>{d?.caseNumber}</span>{caseBlurb(d)}
              </div>
            ))}
          </div>
          <span className='text-[12px] text-navy/[.5]'>Only the case summary is shared, never the client's name or contact details.</span>
        </div>

        <label className='flex flex-col gap-1.5'>
          <span className={label}>Subject</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200}
            className={`${field} h-11 px-3 text-[14px] border-navy/[.2]`} />
        </label>
        <label className='flex flex-col gap-1.5'>
          <span className={label}>Message</span>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} maxLength={10000}
            className={`${field} px-3 py-2.5 text-[13.5px] leading-[1.55] border-navy/[.2] resize-y`} />
          <span className='text-[12px] text-navy/[.5]'>
            {check?.mode === 'login'
              ? '"Log in →" becomes the log-in button.'
              : '"Claim your firm →" becomes the sign-up button (a personal link that pre-selects this firm).'} Our address and an unsubscribe link are added automatically.
          </span>
        </label>

        {error && <div className='p-3 rounded-md bg-danger/[.07] border border-danger/[.3] text-[13px] leading-[1.5] text-danger'>{error}</div>}
      </div>

      <div className='flex-none flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2.5 py-4 px-[22px] bg-white border-t border-navy/[.1] font-plexsans'>
        <button type='button' onClick={onClose} disabled={sending}
          className='w-full sm:w-auto font-semibold text-[13px] leading-none rounded-md py-3 px-[18px] cursor-pointer text-navy bg-white border border-navy/[.2] hover:bg-navy/[.04] disabled:opacity-50'>Cancel</button>
        <button type='button' onClick={send} disabled={!canSend}
          className='w-full sm:w-auto inline-flex items-center justify-center gap-2 font-semibold text-[13px] leading-none rounded-md py-3 px-[18px] text-ivory bg-brass border border-brass enabled:hover:bg-brassDark enabled:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'>
          {sending ? <><QcrLoader variant='draw' size='sm' tone='onBrass' />Sending…</> : 'Send email'}
        </button>
      </div>
    </ReactModal>
  );
}

export default OutreachEmailModal;
